/**
 * Typed oRPC API client for `apps/api`.
 *
 * Architecture refs:
 *   §3 — apps/api owns the oRPC router. Clients consume the typed contract.
 *
 * Strategy:
 *   - Import `AppRouter` (the oRPC `os.router(...)` return type) from
 *     `@proof/api/rpc/router`. This gives us a compile-time contract that
 *     tracks whatever procedures exist server-side.
 *   - Wrap `@orpc/client`'s `createORPCClient` + `RPCLink` so consumers pass
 *     just `{ baseUrl, fetch?, headers? }` and get a typed client whose
 *     methods mirror the server router.
 *   - The router is empty at Track 1.C; when procedures land in Track 2.D
 *     (`recipe.*`, `branch.*`, `attempt.*`, `artefact.*`, `batch.*`) consumers
 *     get typed access without a client-code change. Contract drift (e.g. a
 *     removed or renamed procedure) is a compile error here and in the web
 *     app, which is the "fail to compile on contract drift" acceptance test.
 *
 * Auth: requests are sent with `credentials: "include"` so the server
 * session cookie rides along. Mutating calls additionally attach the CSRF
 * double-submit header via `getCsrfHeader()` — see `../auth/csrf.ts`.
 */

import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import type { AppRouter } from "@proof/api/rpc/router-type";

import { getCsrfHeader } from "../auth/csrf.js";

/**
 * Fully-typed client against the server oRPC router.
 * Shape is inferred at compile time from `AppRouter`.
 */
export type ApiClient = RouterClient<AppRouter>;

export interface ApiClientOptions {
  /** API origin, e.g. `https://proofcook.com`. No trailing slash required. */
  baseUrl: string;
  /** Path prefix where the oRPC router is mounted. Defaults to `/rpc`. */
  rpcPath?: string;
  /** Override the global fetch — tests + MSW; defaults to `globalThis.fetch`. */
  fetch?: typeof fetch;
  /** Extra headers appended to every request (e.g. debug traces). */
  headers?: Record<string, string>;
}

/**
 * Construct a typed oRPC client against the `apps/api` router.
 *
 * Example (once Track 2.D lands):
 *   const api = createApiClient({ baseUrl: "https://proofcook.com" });
 *   const recipes = await api.recipe.list({ limit: 20 });
 *
 * Same-origin note: when `baseUrl` is `""` (same-origin deployment), the
 * combined URL is `"/rpc"` — a relative path. oRPC's `RPCLink` calls
 * `new URL(url)` internally, which requires an absolute URL. We resolve
 * against `window.location.origin` in browser environments so the client
 * works correctly when `VITE_API_BASE_URL` is unset.
 */
export function createApiClient(opts: ApiClientOptions): ApiClient {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  const rpcPath = opts.rpcPath ?? "/rpc";

  const rawUrl = `${baseUrl}${rpcPath}`;
  // oRPC's RPCLink does `new URL(url)` internally. A bare relative path like
  // "/rpc" fails that call with "Invalid URL". When the URL is relative (no
  // scheme), prefix the browser's origin so the absolute form is used.
  const resolvedUrl =
    rawUrl.startsWith("/") && typeof window !== "undefined"
      ? `${window.location.origin}${rawUrl}`
      : rawUrl;

  const link = new RPCLink({
    url: resolvedUrl,
    fetch: (request, init) => {
      // Compose CSRF + session cookie onto every call.
      const method = request.method.toUpperCase();
      const headers = new Headers(request.headers);
      for (const [k, v] of Object.entries(opts.headers ?? {})) {
        headers.set(k, v);
      }
      if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
        const csrf = getCsrfHeader();
        if (csrf) headers.set("X-CSRF-Token", csrf);
      }
      const withHeaders = new Request(request, { headers });
      const f = opts.fetch ?? globalThis.fetch;
      // RPCLink does not include credentials by default — attach here so the
      // session cookie rides on every oRPC call (architecture §4.12).
      return f(withHeaders, { ...init, credentials: "include" });
    },
  });

  return createORPCClient<ApiClient>(link);
}
